import { useEffect, ReactElement } from 'react';

import { PostDocumentUploadResponse } from '@iwoca/lapi-client/edge';
import { clsx } from 'clsx';

import styles from './DocumentRequirement.module.css';
import Required from './Required';
import { useGetDocuments } from '../../../api/lendingApi/lendingApiQueries';
import { useContextStateKey } from '../../../hooks/useContextStateKey';
import { useFileUploadReducer } from '../../../hooks/useFileUploadReducer';
import { TDocumentTypeDE } from '../../../lapiOverrideTypesDE';
import { TOfferDataRequirement } from '../../../types';
import UploadButton from '../UploadButton/UploadButton';
import UploadedFiles from '../UploadedFiles/UploadedFiles';

type TProps = {
  isOptional?: boolean;
  title?: string | React.ReactElement;
  children: ReactElement;
  className?: string;
  documentType?:
    PostDocumentUploadResponse['data']['document_type'] | TDocumentTypeDE;
  status?: TOfferDataRequirement['actions'][number]['status'];
  customButton?: React.ReactNode;
  buttonText?: React.ReactNode;
  isSubmitted?: boolean;
  isColumnLayout?: boolean;
};

const DocumentRequirement = ({
  isOptional,
  title,
  children,
  className,
  documentType,
  customButton,
  buttonText,
  status,
  isSubmitted,
  isColumnLayout,
}: TProps) => {
  const stateKey = useContextStateKey();
  const { documents } = useGetDocuments(stateKey);

  const {
    uploadedFiles,
    setAllFiles,
    setFileLoading,
    setFileSuccess,
    setFileError,
    setFileReject,
    removeFile,
  } = useFileUploadReducer();

  useEffect(() => {
    if (documents) {
      const documentsForThisDocumentType = documents.data.filter(
        (document) => document.attributes.document_type === documentType,
      );
      setAllFiles(documentsForThisDocumentType);
    }
  }, [documentType, documents]);

  const renderRequiredError = () => {
    if (
      isSubmitted &&
      !isOptional &&
      (status === 'required' || status === 'error')
    ) {
      return <Required />;
    }
  };

  return (
    <div
      id={`document-requirement-${documentType}`}
      className={clsx(
        {
          [styles.optionalDocumentContainer]: isOptional,
          [styles.requiredDocumentContainer]: !isOptional,
        },
        className,
      )}
    >
      <div
        className={clsx(styles.documentRequirement, {
          [styles.columnLayout]: isColumnLayout,
        })}
      >
        <div className={styles.descriptionContainer}>
          {title && (
            <h3
              className={
                isOptional ? styles.optionalTitle : styles.requiredTitle
              }
            >
              {title}
              {renderRequiredError()}
            </h3>
          )}
          <div className={styles.description}>{children}</div>
          {uploadedFiles && (
            <UploadedFiles files={uploadedFiles} removeFile={removeFile} />
          )}
        </div>
        <div className={styles.buttonContainer}>
          {customButton ? (
            customButton
          ) : (
            <UploadButton
              documentType={documentType}
              setFileLoading={setFileLoading}
              setFileSuccess={setFileSuccess}
              setFileError={setFileError}
              setFileReject={setFileReject}
              isOptional={isOptional}
              buttonText={buttonText}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentRequirement;
